"use client";

/**
 * React Bits Stepper indicators (TS-TW), adapted for Platform Action strip usage.
 * Visual source: https://reactbits.dev/components/stepper
 *
 * - complete / current (active): SQ green gradient
 * - opened (focus): Secondary CTA surface (see apps/web secondary-cta-classname)
 * - inactive: dark circle + light number (when numbers enabled)
 * - all circles: Secondary CTA elevation (shadow + inset ring)
 * - optional progress ring: translucent IntentProgressCircle over the circle
 * - horizontal (default) or vertical layout with compact circles
 * - horizontal @[40rem]+: interlocking arrow strip (see stepper-arrow-strip)
 */

import { CircleCheck, Redo2 } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { Fragment, type ReactNode, type SVGProps } from "react";
import { cn } from "../../lib";
import { SQ_GREEN_LINEAR_GRADIENT_CLASS } from "../../lib/sq-color-runtime-exports";
import { IntentProgressCircle } from "../intent-ui/progress-circle";
import {
	StepperArrowStrip,
	type StepperArrowStripStep,
} from "./stepper-arrow-strip";

export type StepperStepStatus =
	| "complete"
	| "skipped"
	| "current"
	| "focus"
	| "upcoming"
	| "blocked";

export type StepperOrientation = "horizontal" | "vertical";
export type StepperSize = "default" | "compact";

export interface StepperStep {
	id: string;
	title: string;
	status?: StepperStepStatus;
	/**
	 * Optional 0–100 completion across required + optional tasks for this step.
	 * When set, a translucent progress ring may render inside the circle.
	 */
	progressPercent?: number | null;
	/**
	 * When true, all required tasks are done (tick eligible). Optional tasks may
	 * still be incomplete — progress ring can still show when the step is selected.
	 */
	requiredComplete?: boolean;
	/**
	 * Supporting line under the title in the wide arrow layout (e.g. "3 tasks").
	 * Ignored by the circle strip (titles stay below circles).
	 */
	subtext?: string;
	/**
	 * Non-interactive section header between task rows (vertical rails).
	 * Defaults to a normal step.
	 */
	kind?: "step" | "groupHeader";
	/** Group header supporting line (vertical rails). */
	description?: string;
	/** Optional trailing label on group headers (e.g. "9 of 14 tasks completed (64%)"). */
	trailingLabel?: string;
	/** When true, show a completed checkmark before {@link trailingLabel}. */
	trailingComplete?: boolean;
}

/**
 * What to paint inside each step circle.
 * Defaults keep the historical horizontal strip look (numbers + icons).
 */
export interface StepperIndicatorContent {
	/** Status dots (active / opened) and complete check. Default true. */
	icons?: boolean;
	/** Step index for inactive circles. Default true. */
	numbers?: boolean;
}

export interface StepperProps {
	steps: StepperStep[];
	activeStepId?: string | null;
	ariaLabel?: string;
	className?: string;
	stepContainerClassName?: string;
	/** Layout axis. Default `horizontal`. */
	orientation?: StepperOrientation;
	/** Circle size. Vertical rails typically use `compact`. Default `default`. */
	size?: StepperSize;
	/**
	 * Circle interior glyphs. Omit for `{ icons: true, numbers: true }`.
	 * Example (task rail): `{ icons: true, numbers: false }`.
	 */
	indicatorContent?: StepperIndicatorContent;
	/** When false, hides the step title beside/below the circle. Default true. */
	showLabels?: boolean;
	/** When true, indicators are non-interactive (React Bits disabled look). */
	disableStepIndicators?: boolean;
	/**
	 * When `disableStepIndicators` is true, also apply reduced opacity.
	 * Default true. Set false for non-clickable indicators that should stay fully visible
	 * (e.g. vertical task rail).
	 */
	dimDisabledIndicators?: boolean;
	onStepClick?: (stepId: string) => void;
	renderStepAccessory?: (step: StepperStep) => ReactNode;
	/**
	 * Vertical layout: main content column to the right of each circle
	 * (e.g. task row). Horizontal layout: ignored (use accessory / labels).
	 */
	renderStepContent?: (step: StepperStep, index: number) => ReactNode;
}

/** Visual states for the circle (opened = accordion expanded / focus). */
type IndicatorStatus =
	| "active"
	| "opened"
	| "inactive"
	| "complete"
	| "skipped";

const INACTIVE_BG = "#222";
const INACTIVE_FG = "#e5e5e5";

/** Matches Learn TopicContinuationNavRing visibility motion. */
const PROGRESS_RING_VISIBILITY_MS = 300;
const PROGRESS_RING_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const CIRCLE_SIZE_PX: Record<StepperSize, number> = {
	default: 32,
	compact: 24,
};

/**
 * Mirrors `SECONDARY_CTA_GRADIENT_SURFACE` + `CTA_SECONDARY_ELEVATION_CLASS`
 * from `apps/web/modules/shared/lib/cta` (ui package cannot import app paths).
 */
const SECONDARY_CTA_SURFACE_CLASS = cn(
	"bg-gradient-to-b from-white via-neutral-50 to-neutral-100 text-neutral-900",
	"dark:from-[#243652] dark:via-[#1a2435] dark:to-[#0f1419] dark:text-white",
);

const SECONDARY_CTA_ELEVATION_CLASS = cn(
	"shadow-[0_1px_2px_rgb(15_23_42/0.08),0_3px_10px_rgb(15_23_42/0.06)]",
	"ring-1 ring-inset ring-white/50 dark:ring-white/12",
);

function toIndicatorStatus(
	status: StepperStepStatus | undefined,
	isActiveFallback: boolean,
): IndicatorStatus {
	if (status === "focus") {
		return "opened";
	}
	if (status === "skipped") {
		return "skipped";
	}
	if (status === "complete") {
		return "complete";
	}
	if (status === "current") {
		return "active";
	}
	if (status === "upcoming" || status === "blocked") {
		return "inactive";
	}
	return isActiveFallback ? "active" : "inactive";
}

function clampProgressPercent(value: number): number {
	return Math.max(0, Math.min(100, value));
}

function resolveIndicatorContent(
	content: StepperIndicatorContent | undefined,
): Required<StepperIndicatorContent> {
	return {
		icons: content?.icons ?? true,
		numbers: content?.numbers ?? true,
	};
}

/**
 * Horizontal or vertical step strip using React Bits indicator/connector visuals.
 * Presentational only — parents own navigation / graphRevision.
 */
export function Stepper({
	steps,
	activeStepId,
	ariaLabel = "Progress steps",
	className,
	stepContainerClassName,
	orientation = "horizontal",
	size = "default",
	indicatorContent,
	showLabels = true,
	disableStepIndicators: disableStepIndicatorsProp,
	dimDisabledIndicators = true,
	onStepClick,
	renderStepAccessory,
	renderStepContent,
}: StepperProps) {
	const disableStepIndicators =
		disableStepIndicatorsProp ?? typeof onStepClick !== "function";
	const glyphs = resolveIndicatorContent(indicatorContent);
	const isVertical = orientation === "vertical";
	const circleSizePx = CIRCLE_SIZE_PX[size];

	const arrowSteps: StepperArrowStripStep[] = isVertical
		? []
		: steps
				.filter((step) => step.kind !== "groupHeader")
				.map((step) => ({
					id: step.id,
					title: step.title,
					subtext: step.subtext,
					status: toIndicatorStatus(
						step.status,
						step.id === activeStepId,
					),
					progressPercent: step.progressPercent,
					requiredComplete: step.requiredComplete,
					blocked: step.status === "blocked",
				}));

	const circleStrip = (
		<div
			className={cn(
				isVertical
					? "flex w-full flex-col"
					: cn(
							// Horizontal: scroll when circles+connectors exceed strip width.
							"w-full overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:thin]",
							"@[40rem]:hidden",
						),
				stepContainerClassName,
			)}
			data-stepper-layout={isVertical ? "vertical" : "circles"}
		>
			<div
				className={cn(
					isVertical
						? "flex w-full flex-col"
						: cn(
								"flex w-max min-w-full items-start",
								showLabels && "pb-5",
							),
				)}
			>
			{steps.map((step, index) => {
				if (step.kind === "groupHeader") {
					if (!isVertical) {
						return null;
					}

					return (
						<div
							key={step.id}
							className="relative flex min-w-0 items-center justify-between gap-3 pb-2 pt-3.5 first:pt-0"
							data-stepper-step={step.id}
							data-stepper-kind="groupHeader"
							role="presentation"
						>
							<p
								id={`${step.id}-label`}
								className="min-w-0 truncate text-[10px] leading-snug text-muted-foreground"
							>
								<span className="font-semibold uppercase tracking-[0.14em]">
									{step.title}
								</span>
								{step.description ? (
									<>
										<span className="mx-1.5 font-normal tracking-normal">
											–
										</span>
										<span className="font-normal tracking-normal text-muted-foreground/80">
											{step.description}
										</span>
									</>
								) : null}
							</p>
							{step.trailingLabel ? (
								<span
									className={cn(
										"inline-flex shrink-0 items-center gap-1 text-[10px] font-medium tabular-nums",
										step.trailingComplete
											? "text-foreground"
											: "text-muted-foreground",
									)}
									data-testid={`stepper-group-progress-${step.id}`}
								>
									{step.trailingComplete ? (
										<CircleCheck
											className="size-3 shrink-0 text-[var(--sq-green-static-to)]"
											aria-hidden
										/>
									) : null}
									{step.trailingLabel}
								</span>
							) : null}
						</div>
					);
				}

				const status = toIndicatorStatus(
					step.status,
					step.id === activeStepId,
				);
				/** Vertical: only connect within a group. Horizontal: skip group headers. */
				const nextStep = steps[index + 1];
				const connectToNextTask =
					nextStep != null && nextStep.kind !== "groupHeader";
				const isNotLastStep = steps
					.slice(index + 1)
					.some((later) => later.kind !== "groupHeader");
				const connectorComplete =
					status === "complete" ||
					status === "skipped" ||
					(status === "opened" &&
						steps
							.slice(index + 1)
							.some(
								(later) =>
									later.kind !== "groupHeader" &&
									(later.status === "complete" ||
										later.status === "skipped" ||
										later.status === "current" ||
										later.status === "focus"),
							));

				const indicator = (
					<StepIndicator
						step={index + 1}
						status={status}
						size={size}
						circleSizePx={circleSizePx}
						showIcons={glyphs.icons}
						showNumbers={glyphs.numbers}
						progressPercent={step.progressPercent}
						requiredComplete={step.requiredComplete === true}
						disableStepIndicators={
							disableStepIndicators || step.status === "blocked"
						}
						dimDisabledIndicators={
							step.status === "blocked"
								? true
								: dimDisabledIndicators
						}
						onClickStep={() => onStepClick?.(step.id)}
					/>
				);

				if (isVertical) {
					return (
						<div
							key={step.id}
							className="relative flex min-w-0 gap-2"
							data-stepper-step={step.id}
							data-stepper-status={step.status ?? status}
						>
							<div
								className="flex shrink-0 flex-col items-center self-stretch"
								style={{ width: circleSizePx }}
							>
								{indicator}
								{connectToNextTask ? (
									<StepConnector
										orientation="vertical"
										isComplete={connectorComplete}
									/>
								) : null}
							</div>
							<div
								className={cn(
									"min-w-0 flex-1",
									connectToNextTask && "pb-0.5",
								)}
							>
								{showLabels ? (
									<span
										className="flex items-center truncate text-sm font-medium text-foreground"
										style={{ minHeight: circleSizePx }}
									>
										{step.title}
									</span>
								) : null}
								{renderStepContent?.(step, index)}
								{renderStepAccessory?.(step)}
							</div>
						</div>
					);
				}

				return (
					<Fragment key={step.id}>
						<div
							className="relative flex shrink-0 flex-col items-center"
							style={{ width: circleSizePx }}
							data-stepper-step={step.id}
							data-stepper-status={step.status ?? status}
						>
							{indicator}
							{showLabels ? (
								<span className="pointer-events-none absolute top-full left-1/2 z-[1] mt-1 w-[5.5rem] max-w-[5.5rem] -translate-x-1/2 truncate text-center text-[10px] text-muted-foreground">
									{step.title}
								</span>
							) : null}
							{renderStepAccessory?.(step)}
						</div>
						{isNotLastStep ? (
							<div
								className="mx-1.5 flex min-w-3 flex-1 items-center self-start"
								style={{ height: circleSizePx }}
								aria-hidden
							>
								<StepConnector
									orientation="horizontal"
									isComplete={connectorComplete}
								/>
							</div>
						) : null}
					</Fragment>
				);
			})}
			</div>
		</div>
	);

	return (
		<nav
			aria-label={ariaLabel}
			className={cn("w-full min-w-0", className)}
			data-testid="react-bits-stepper"
			data-orientation={orientation}
			data-size={size}
		>
			{circleStrip}
			{!isVertical && arrowSteps.length > 0 ? (
				<div className="hidden w-full min-w-0 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:thin] @[40rem]:block">
					<StepperArrowStrip
						steps={arrowSteps}
						ariaLabel={ariaLabel}
						disableStepIndicators={disableStepIndicators}
						dimDisabledIndicators={dimDisabledIndicators}
						onStepClick={onStepClick}
					/>
				</div>
			) : null}
		</nav>
	);
}

interface StepIndicatorProps {
	step: number;
	status: IndicatorStatus;
	size: StepperSize;
	circleSizePx: number;
	showIcons: boolean;
	showNumbers: boolean;
	progressPercent?: number | null;
	requiredComplete?: boolean;
	onClickStep: () => void;
	disableStepIndicators?: boolean;
	dimDisabledIndicators?: boolean;
}

function StepIndicatorInnerGlyph({
	step,
	status,
	size,
	showIcons,
	showNumbers,
}: {
	step: number;
	status: IndicatorStatus;
	size: StepperSize;
	showIcons: boolean;
	showNumbers: boolean;
	requiredComplete: boolean;
}) {
	const checkClass = size === "compact" ? "h-3 w-3" : "h-4 w-4";
	const dotClass = size === "compact" ? "h-2 w-2" : "h-3 w-3";
	const numberClass = size === "compact" ? "text-[10px]" : "text-xs";

	/** Selected / current always show the live glyph (dot), even when required work is done. */
	if (status === "active" || status === "opened") {
		if (showIcons) {
			return (
				<div
					className={cn(
						"rounded-full",
						dotClass,
						status === "opened"
							? "bg-neutral-900 dark:bg-white"
							: "bg-white",
					)}
				/>
			);
		}
		if (showNumbers) {
			return (
				<span
					className={cn(
						numberClass,
						status === "opened"
							? "text-neutral-900 dark:text-neutral-200"
							: "text-white",
					)}
				>
					{step}
				</span>
			);
		}
		return null;
	}

	/** Skipped: filled circle with redo icon (terminal, counts as done). */
	if (status === "skipped" && showIcons) {
		return (
			<Redo2
				className={cn(checkClass, "text-white")}
				aria-hidden
				strokeWidth={2.5}
			/>
		);
	}

	if (status === "complete" && showIcons) {
		return <CheckIcon className={cn(checkClass, "text-white")} />;
	}

	if (status === "complete" || status === "skipped") {
		if (showNumbers) {
			return (
				<span className={cn(numberClass, "text-neutral-200")}>
					{step}
				</span>
			);
		}
		return null;
	}

	// inactive
	if (showNumbers) {
		return (
			<span className={cn(numberClass, "text-neutral-200")}>{step}</span>
		);
	}
	return null;
}

function StepIndicator({
	step,
	status,
	size,
	circleSizePx,
	showIcons,
	showNumbers,
	progressPercent,
	requiredComplete = false,
	onClickStep,
	disableStepIndicators = false,
	dimDisabledIndicators = true,
}: StepIndicatorProps) {
	function handleClick() {
		if (disableStepIndicators) {
			return;
		}
		onClickStep();
	}

	const isOpened = status === "opened";
	/** Selected (opened) wins over requiredComplete so tick ↔ selected style can toggle. */
	const treatAsComplete =
		!isOpened && (status === "complete" || status === "skipped");
	const usesGreenGradient = treatAsComplete || status === "active";
	const hasProgressProp =
		typeof progressPercent === "number" && Number.isFinite(progressPercent);
	const clampedProgress = hasProgressProp
		? clampProgressPercent(progressPercent)
		: 0;
	const isSelected = status === "opened" || status === "active";
	const allTasksDone = clampedProgress >= 100;
	/**
	 * Hide when all tasks done. Otherwise: selected always (incl. tick + optional
	 * leftover); or any partial when required work remains.
	 */
	const showProgressRing =
		hasProgressProp &&
		!allTasksDone &&
		(isSelected || (!requiredComplete && clampedProgress > 0));
	/** Dark ring on light opened face; white ring on green / dark faces. */
	const progressOnLightFace = isOpened;
	const progressStrokeWidth = size === "compact" ? 8 : 10;
	const progressFillWidth = size === "compact" ? 5.5 : 6;
	const openedScale = size === "compact" ? 1.04 : 1.05;
	const indicatorAnimate =
		status === "opened" ? "opened" : treatAsComplete ? "complete" : status;

	return (
		<motion.div
			role="button"
			tabIndex={disableStepIndicators ? -1 : 0}
			aria-current={
				status === "active" || status === "opened" ? "step" : undefined
			}
			onClick={handleClick}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					handleClick();
				}
			}}
			className={cn(
				"relative outline-none focus:outline-none",
				disableStepIndicators
					? dimDisabledIndicators
						? "cursor-not-allowed opacity-50"
						: "cursor-default"
					: "cursor-pointer",
			)}
			animate={indicatorAnimate}
			initial={false}
		>
			<motion.div
				variants={{
					inactive: {
						scale: 1,
						backgroundColor: INACTIVE_BG,
						color: INACTIVE_FG,
					},
					active: {
						scale: 1,
						backgroundColor: "var(--sq-green-static-from)",
						color: "#ffffff",
					},
					complete: {
						scale: 1,
						backgroundColor: "var(--sq-green-static-from)",
						color: "#ffffff",
					},
					// Transparent so Secondary CTA gradient class paints the fill.
					opened: {
						scale: openedScale,
						backgroundColor: "rgba(0,0,0,0)",
						color: "currentColor",
					},
				}}
				transition={{ duration: 0.3 }}
				className={cn(
					"relative flex items-center justify-center rounded-full font-semibold",
					size === "compact" ? "h-6 w-6" : "h-8 w-8",
					SECONDARY_CTA_ELEVATION_CLASS,
					usesGreenGradient && SQ_GREEN_LINEAR_GRADIENT_CLASS,
					isOpened && SECONDARY_CTA_SURFACE_CLASS,
				)}
			>
				<StepIndicatorInnerGlyph
					step={step}
					status={status}
					size={size}
					showIcons={showIcons}
					showNumbers={showNumbers}
					requiredComplete={requiredComplete}
				/>
				{hasProgressProp ? (
					<span
						aria-hidden={!showProgressRing}
						className={cn(
							"pointer-events-none absolute inset-0 z-[1] flex origin-center items-center justify-center [contain:layout]",
							"motion-reduce:!transition-none",
							progressOnLightFace
								? "[--sq-step-progress:rgb(15_23_42/0.45)] dark:[--sq-step-progress:rgb(255_255_255/0.75)]"
								: "[--sq-step-progress:rgb(255_255_255/0.75)]",
						)}
						style={{
							transform: showProgressRing
								? "scale(1)"
								: "scale(0)",
							opacity: showProgressRing ? 1 : 0,
							backfaceVisibility: "hidden",
							transitionProperty: "transform, opacity",
							transitionDuration: `${PROGRESS_RING_VISIBILITY_MS}ms`,
							transitionTimingFunction: PROGRESS_RING_EASING,
						}}
					>
						<IntentProgressCircle
							value={clampedProgress}
							size={circleSizePx}
							strokeWidth={progressStrokeWidth}
							progressStrokeWidth={progressFillWidth}
							variant="learn"
							segmentCap="square"
							learnProgressColor="var(--sq-step-progress)"
							showTrack={false}
							showPattern={false}
							className="shrink-0"
						/>
					</span>
				) : null}
			</motion.div>
		</motion.div>
	);
}

interface StepConnectorProps {
	isComplete: boolean;
	orientation: StepperOrientation;
}

function StepConnector({ isComplete, orientation }: StepConnectorProps) {
	const isVertical = orientation === "vertical";
	const lineVariants: Variants = isVertical
		? {
				incomplete: { height: 0, backgroundColor: "transparent" },
				complete: {
					height: "100%",
					backgroundColor: "var(--sq-green-static-from)",
				},
			}
		: {
				incomplete: { width: 0, backgroundColor: "transparent" },
				complete: {
					width: "100%",
					backgroundColor: "var(--sq-green-static-from)",
				},
			};

	return (
		<div
			className={cn(
				"relative overflow-hidden rounded bg-neutral-600",
				isVertical
					? "mx-auto my-0.5 min-h-[0.5rem] w-0.5 flex-1"
					: "h-0.5 w-full min-w-3",
			)}
			aria-hidden
		>
			<motion.div
				className={cn(
					"absolute",
					isVertical ? "top-0 left-0 w-full" : "top-0 left-0 h-full",
				)}
				variants={lineVariants}
				initial={false}
				animate={isComplete ? "complete" : "incomplete"}
				transition={{ duration: 0.4 }}
			/>
		</div>
	);
}

function CheckIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg
			aria-hidden="true"
			{...props}
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			viewBox="0 0 24 24"
		>
			<motion.path
				initial={{ pathLength: 0 }}
				animate={{ pathLength: 1 }}
				transition={{
					delay: 0.1,
					type: "tween",
					ease: "easeOut",
					duration: 0.3,
				}}
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M5 13l4 4L19 7"
			/>
		</svg>
	);
}

export default Stepper;
